'use client';

import React, { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  category: string;
  caption?: string;
  culturalContext?: string;
}

interface LightboxProps {
  image: GalleryImage | null;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  currentIndex: number;
  totalImages: number;
}

const Lightbox = ({ image, onClose, onNext, onPrevious, currentIndex, totalImages }: LightboxProps) => {
  useEffect(() => {
    if (!image) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrevious();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [image, onClose, onNext, onPrevious]);

  if (!image) return null;

  return (
    <div className="fixed inset-0 z-50 bg-foreground/95 flex items-center justify-center p-4">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-card/10 hover:bg-card/20 text-white transition-colors duration-200 z-10"
        aria-label="Close lightbox"
      >
        <Icon name="XMarkIcon" size={32} />
      </button>

      {/* Previous Button */}
      {totalImages > 1 && (
        <button
          onClick={onPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-card/10 hover:bg-card/20 text-white transition-colors duration-200 z-10"
          aria-label="Previous image"
        >
          <Icon name="ChevronLeftIcon" size={32} />
        </button>
      )}

      {/* Next Button */}
      {totalImages > 1 && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-card/10 hover:bg-card/20 text-white transition-colors duration-200 z-10"
          aria-label="Next image"
        >
          <Icon name="ChevronRightIcon" size={32} />
        </button>
      )}

      {/* Image Container */}
      <div className="max-w-6xl w-full max-h-[90vh] flex flex-col">
        <div className="relative flex-1 flex items-center justify-center mb-4">
          <AppImage
            src={image.src}
            alt={image.alt}
            className="max-w-full max-h-[70vh] object-contain rounded-lg"
          />
        </div>

        {/* Image Info */}
        <div className="bg-card/10 backdrop-blur-sm rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-3">
            <p className="font-inter text-sm opacity-80">
              {currentIndex + 1} / {totalImages}
            </p>
          </div>
          
          {image.caption && (
            <h3 className="font-playfair text-xl font-semibold mb-2">
              {image.caption}
            </h3>
          )}
          
          {image.culturalContext && (
            <p className="font-inter text-sm opacity-90 leading-relaxed">
              {image.culturalContext}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Lightbox;